import React, { createContext, useContext, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  sendChatMessage,
  getChatHistory,
  type ChatMessage,
  type ChatContext as ChatContextType,
} from "@/lib/chatApi";

// =============================================================================
// TYPES
// =============================================================================

interface ChatState {
  isOpen: boolean;
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  conversationId: string | null;
  remainingMessages: number | null;
  context: ChatContextType;
}

interface GlobalChatContextValue extends ChatState {
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  sendMessage: (content: string, techniqueId?: string) => Promise<void>;
  clearHistory: () => void;
  setContext: (ctx: Partial<ChatContextType>) => void;
  loadConversation: (conversationId: string) => Promise<void>;
  pageContext: string;
}

// =============================================================================
// CONTEXT
// =============================================================================

const GlobalChatContext = createContext<GlobalChatContextValue | null>(null);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Detect page context from URL pathname
 */
function getPageContext(pathname: string): string {
  if (pathname.includes("/dashboard")) return "dashboard";
  if (pathname.includes("/practice")) return "practice";
  if (pathname.includes("/arena") || pathname.includes("/tournament"))
    return "arena";
  if (pathname.includes("/technique")) return "technique";
  if (pathname.includes("/leaderboard")) return "leaderboard";
  if (pathname.includes("/roadmap")) return "roadmap";
  return "general";
}

// =============================================================================
// PROVIDER
// =============================================================================

interface GlobalChatProviderProps {
  children: React.ReactNode;
}

export function GlobalChatProvider({ children }: GlobalChatProviderProps) {
  const location = useLocation();
  const pageContext = getPageContext(location.pathname);

  const [state, setState] = useState<ChatState>({
    isOpen: false,
    messages: [],
    isLoading: false,
    error: null,
    conversationId: null,
    remainingMessages: null,
    context: {},
  });

  const openChat = useCallback(async () => {
    setState((prev) => ({ ...prev, isOpen: true }));

    // Try to load recent general conversation if no messages
    if (!state.conversationId && state.messages.length === 0) {
      // For now, don't auto-load previous conversations to keep it simple
      // Users start fresh each time they open the global chat
    }
  }, [state.conversationId, state.messages.length]);

  const closeChat = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const toggleChat = useCallback(() => {
    if (state.isOpen) {
      closeChat();
    } else {
      openChat();
    }
  }, [state.isOpen, openChat, closeChat]);

  const sendMessage = useCallback(
    async (content: string, techniqueId?: string) => {
      if (!content.trim()) return;

      const userMessage: ChatMessage = {
        role: "user",
        content: content.trim(),
        timestamp: new Date().toISOString(),
      };

      // Add user message immediately
      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, userMessage],
        isLoading: true,
        error: null,
      }));

      try {
        const response = await sendChatMessage({
          techniqueId,
          message: content.trim(),
          conversationId: state.conversationId || undefined,
          context: state.context,
          pageContext,
        });

        if (!response.success) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: response.error || "Failed to get response",
          }));
          return;
        }

        const assistantMessage: ChatMessage = {
          role: "assistant",
          content: response.reply || "",
          timestamp: new Date().toISOString(),
        };

        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, assistantMessage],
          isLoading: false,
          conversationId: response.conversationId || prev.conversationId,
          remainingMessages:
            response.remainingMessages ?? prev.remainingMessages,
        }));
      } catch (err: any) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: err.message || "An unexpected error occurred",
        }));
      }
    },
    [state.conversationId, state.context, pageContext],
  );

  const clearHistory = useCallback(() => {
    setState((prev) => ({
      ...prev,
      messages: [],
      conversationId: null,
      error: null,
    }));
  }, []);

  const setContext = useCallback((ctx: Partial<ChatContextType>) => {
    setState((prev) => ({
      ...prev,
      context: { ...prev.context, ...ctx },
    }));
  }, []);

  const loadConversation = useCallback(async (conversationId: string) => {
    const history = await getChatHistory(conversationId);
    setState((prev) => ({
      ...prev,
      conversationId,
      messages: history,
    }));
  }, []);

  const value: GlobalChatContextValue = {
    ...state,
    openChat,
    closeChat,
    toggleChat,
    sendMessage,
    clearHistory,
    setContext,
    loadConversation,
    pageContext,
  };

  return (
    <GlobalChatContext.Provider value={value}>
      {children}
    </GlobalChatContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useGlobalChat() {
  const context = useContext(GlobalChatContext);

  if (!context) {
    throw new Error("useGlobalChat must be used within a GlobalChatProvider");
  }

  return context;
}
